import { IS_PRODUCTION } from '@shared/utils/constant'

type TranslationFunction = (key: string) => string
type AppManifestUpdate = { id?: string }

const chunkErrorMessages = [
  'Loading chunk',
  'Importing a module script failed',
  'Failed to fetch dynamically imported module'
]

function isChunkError(error: unknown) {
  if (!(error instanceof Error)) {
    return false
  }

  return chunkErrorMessages.some((message) => error.message.includes(message))
}

export function isNewBuildAvailable(
  meta: undefined | AppManifestUpdate,
  currentBuildId: string
) {
  return Boolean(meta?.id && meta.id !== currentBuildId)
}

export default defineNuxtPlugin({
  name: 'chunk-error',
  dependsOn: ['store'],
  setup(context) {
    const router = useRouter()
    let isNewVersionNotificationVisible = false

    if (!IS_PRODUCTION) {
      return
    }

    const { t } = context.$i18n as { t: TranslationFunction }
    const currentBuildId = useRuntimeConfig().app.buildId

    function onReload() {
      window.location.reload()
    }

    function notifyNewVersionAvailable() {
      if (isNewVersionNotificationVisible) {
        return
      }

      const notificationStore = useSharedNotificationStore()

      isNewVersionNotificationVisible = true

      notificationStore.info({
        timeout: Infinity,
        title: t('toast.newVersionAvailable.title'),
        description: t('toast.newVersionAvailable.description'),
        actions: [
          {
            callback: onReload,
            label: t('toast.newVersionAvailable.reload')
          }
        ]
      })
    }

    router.onError((error) => {
      if (isChunkError(error)) {
        notifyNewVersionAvailable()
      }
    })

    context.hook('app:chunkError', () => {
      notifyNewVersionAvailable()
    })

    context.hook('app:manifest:update', (meta) => {
      if (!isNewBuildAvailable(meta, currentBuildId)) {
        return
      }

      notifyNewVersionAvailable()
    })

    context.hook('app:error', (error) => {
      if (isChunkError(error)) {
        notifyNewVersionAvailable()
      }
    })
  }
})
